import { Flex, Text } from '@chakra-ui/react';
import { Button, FormFeedback, Input } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { Layout } from 'src/components/Layout';
import { Overlay } from 'src/components/Overlay';
import { IArticleType } from '@publishing-engine/domain/models';
import { hasAlphabetCharacter } from './components/FieldEditorModal/helpers';

type IArticleTypeProp = Pick<IArticleType, 'name'> & { id?: string };

const MAX_CHARACTER_LIMIT = 50;

const MIN_CHARACTER_LIMIT = 3;

interface Props {
  articleType: IArticleTypeProp;
  onClose: () => void;
  onSave: (data: IArticleTypeProp) => void;
}

export const EditArticleTypeModal: React.FC<Props> = ({
  articleType,
  onClose,
  onSave
}) => {
  const { register, handleSubmit, getValues, formState } = useForm({
    defaultValues: {
      name: articleType.name
    },
    mode: 'onChange'
  });

  const onSubmit = () => {
    const name = getValues('name');
    if (name) onSave({ name, id: articleType.id });
  };

  return (
    <Overlay>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Layout width={{ base: '100%', md: '960px' } as any}>
          <Flex flexDirection="column" gap={4}>
            <Text textStyle="headingTitle">
              {articleType.id ? 'Edit' : 'Create'} Article Type
            </Text>

            <FieldLabel
              label="Name"
              subText="Choose a name for your Article Type."
              isRequired={false}
            />
            <Input
              {...register('name', {
                validate: value => {
                  const trimmedValue = value?.trim() ?? '';

                  if (!trimmedValue) {
                    return 'Name is required';
                  }

                  if (
                    trimmedValue.length < MIN_CHARACTER_LIMIT ||
                    trimmedValue.length > MAX_CHARACTER_LIMIT
                  ) {
                    return `Name must be between ${MIN_CHARACTER_LIMIT} and ${MAX_CHARACTER_LIMIT} characters`;
                  }

                  if (!hasAlphabetCharacter(trimmedValue)) {
                    return 'Name must contain at least one alphabet and should not consist of only numbers or special characters';
                  }

                  return true;
                }
              })}
              isInvalid={!!formState.errors.name?.message}
            />
            {formState.errors.name?.message ? (
              <FormFeedback variant="error">
                {formState.errors.name.message}
              </FormFeedback>
            ) : null}

            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Exit Without Saving
              </Button>
              <Button type="submit">Create Article Type</Button>
            </Flex>
          </Flex>
        </Layout>
      </form>
    </Overlay>
  );
};
